import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { toast } from 'sonner';
import './MyChildren.css';

export default function MyChildren({ user, language, onProfilesChange, onProfileCreated }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showIllustrationPicker, setShowIllustrationPicker] = useState(null); // Profile ID to change illustration for
  const [storyImages, setStoryImages] = useState([]); // Available story images to pick from
  const [loadingImages, setLoadingImages] = useState(false);
  const [regeneratingReference, setRegeneratingReference] = useState(false);
  const [selectedPhotoPreviewUrl, setSelectedPhotoPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    age: 5, 
    photo: null, 
    description: '',
    relationship: 'main',
    visual_style: 'realistic',
    photo_position_y: 50,
    hair_color: '',
    eye_color: ''
  });

  const isGerman = language === 'de';
  
  useEffect(() => {
    if (!formData.photo) {
      setSelectedPhotoPreviewUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.photo);
    setSelectedPhotoPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.photo]);
  
  // Relationship options
  const RELATIONSHIPS = {
    main: { en: 'Main child', de: 'Hauptkind', icon: '●' },
    sister: { en: 'Sibling', de: 'Geschwisterkind', icon: '◐' },
    brother: { en: 'Sibling', de: 'Geschwisterkind', icon: '◐' },
    friend: { en: 'Friend', de: 'Freund/in', icon: '◆' },
    cousin: { en: 'Cousin', de: 'Cousin/Cousine', icon: '◇' }
  };
  const RELATIONSHIP_OPTIONS = ['main', 'sister', 'friend', 'cousin'];
  const normalizeRelationship = (value) => (value === 'brother' ? 'sister' : (value || 'main'));
  const VISUAL_STYLES = {
    realistic: {
      en: 'Photo-close',
      de: 'Foto-nah',
      descriptionEn: 'Closest match to uploaded photo',
      descriptionDe: 'Moeglichst nah am hochgeladenen Foto'
    },
    illustrative: {
      en: 'Storybook style',
      de: 'Kinderbuchstil',
      descriptionEn: 'More storybook-like and stylized',
      descriptionDe: 'Mehr Kinderbuch-Stil und stilisierter'
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/child-profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedProfiles = response.data.profiles || [];
      setProfiles(loadedProfiles);
      
      // Notify parent component if callback provided
      if (onProfilesChange) {
        onProfilesChange(loadedProfiles);
      }
    } catch (error) {
      console.error('Error loading child profiles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.age) {
      toast.error(isGerman ? 'Bitte alle Felder ausfüllen' : 'Please fill all fields');
      return;
    }

    // Validate: either photo or description/colors must be provided
    if (!editingProfile) {
      const hasManualInfo = formData.description.trim() || (formData.hair_color && formData.eye_color);
      if (!formData.photo && !hasManualInfo) {
        toast.error(isGerman ? 'Bitte Foto hochladen ODER Haar-/Augenfarbe eingeben' : 'Please upload photo OR enter hair/eye color');
        return;
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('age', formData.age);
      formDataToSend.append('relationship', normalizeRelationship(formData.relationship));
      formDataToSend.append('visual_style', formData.visual_style || 'realistic');
      formDataToSend.append('photo_position_y', String(formData.photo_position_y ?? 50));
      
      if (formData.photo) {
        formDataToSend.append('photo', formData.photo);
      }
      
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      
      if (formData.hair_color) {
        formDataToSend.append('hair_color', formData.hair_color);
      }
      
      if (formData.eye_color) {
        formDataToSend.append('eye_color', formData.eye_color);
      }

      if (editingProfile) {
        // Update existing profile
        await axios.put(
          `${API}/child-profiles/${editingProfile.id}`,
          formDataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        toast.success(isGerman ? 'Kindprofil aktualisiert!' : 'Child profile updated!');
      } else {
        // Create new profile
        const createResponse = await axios.post(
          `${API}/child-profiles`,
          formDataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        toast.success(isGerman ? 'Kindprofil erstellt!' : 'Child profile created!');
        
        // Notify parent component about newly created profile
        if (onProfileCreated && createResponse.data && createResponse.data.profile) {
          onProfileCreated(createResponse.data.profile);
        }
      }

      await loadProfiles();
      setShowAddModal(false);
      setEditingProfile(null);
      setFormData({ name: '', age: 5, photo: null, description: '', relationship: 'main', visual_style: 'realistic', photo_position_y: 50, hair_color: '', eye_color: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (isGerman ? 'Fehler beim Speichern' : 'Error saving profile');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (profileId, profileName) => {
    console.error('🚨🚨🚨 DELETE FUNCTION CALLED 🚨🚨🚨');
    console.error('Profile ID:', profileId);
    console.error('Profile Name:', profileName);
    if (!window.confirm(isGerman ? `${profileName} wirklich löschen?` : `Really delete ${profileName}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/child-profiles/${profileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isGerman ? 'Profil gelöscht!' : 'Profile deleted!');
      await loadProfiles();
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast.error(isGerman ? 'Fehler beim Löschen' : 'Error deleting');
    }
  };

  // Load story cover images for illustration picker
  const loadStoryImagesForProfile = async (profile) => {
    setLoadingImages(true);
    try {
      const token = localStorage.getItem('token');
      // Get all stories that feature this child
      const response = await axios.get(`${API}/stories`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50 }
      });
      
      const stories = response.data.stories || response.data || [];
      const normalizeName = (name) => (name || '').toString().trim().toLowerCase();
      const targetName = normalizeName(profile?.name);
      
      // Filter stories that include this child and have cover images
      const relevantImages = [];
      for (const story of stories) {
        // Check if this child is in the story
        const isInStory = 
          normalizeName(story.child_name) === targetName || 
          (story.children && story.children.some(c => normalizeName(c.name) === targetName));
        
        if (isInStory && story.cover_image_url) {
          relevantImages.push({
            storyId: story.id,
            storyTitle: story.title,
            imageUrl: story.cover_image_url,
            createdAt: story.created_at
          });
        }
      }

      // Newest stories first
      relevantImages.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setStoryImages(relevantImages);
    } catch (error) {
      console.error('Error loading story images:', error);
      toast.error(isGerman ? 'Fehler beim Laden der Bilder' : 'Error loading images');
    } finally {
      setLoadingImages(false);
    }
  };

  // Update character illustration URL
  const handleSelectIllustration = async (imageUrl) => {
    if (!showIllustrationPicker) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/child-profiles/${showIllustrationPicker}/illustration`,
        { character_illustration_url: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(isGerman ? 'Referenzbild aktualisiert!' : 'Reference image updated!');
      await loadProfiles();
      setShowIllustrationPicker(null);
      setStoryImages([]);
    } catch (error) {
      console.error('Error updating illustration:', error);
      toast.error(isGerman ? 'Fehler beim Aktualisieren' : 'Error updating');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateReference = async (profileId) => {
    setRegeneratingReference(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/child-profiles/${profileId}/regenerate-reference`,
        { visual_style: formData.visual_style || 'realistic' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        isGerman
          ? 'Neues Referenzbild wurde erzeugt'
          : 'New reference image was generated'
      );

      if (response.data?.character_illustration_url) {
        await loadProfiles();
      }
    } catch (error) {
      console.error('Error regenerating reference:', error);
      toast.error(
        error.response?.data?.detail ||
        (isGerman ? 'Referenzbild konnte nicht erzeugt werden' : 'Failed to regenerate reference image')
      );
    } finally {
      setRegeneratingReference(false);
    }
  };

  // Open illustration picker
  const openIllustrationPicker = async (profile) => {
    setShowIllustrationPicker(profile.id);
    await loadStoryImagesForProfile(profile);
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({ 
      name: profile.name, 
      age: profile.age, 
      photo: null,
      description: profile.character_description || '',
      relationship: normalizeRelationship(profile.relationship),
      visual_style: profile.visual_style || 'realistic',
      photo_position_y: profile.photo_position_y ?? 50,
      hair_color: profile.hair_color || '',
      eye_color: profile.eye_color || ''
    });
    setShowAddModal(true);
  };

  return (
    <div className="my-children-section">
      <div className="section-header">
        <h2>👨‍👩‍👧‍👦 {isGerman ? 'Meine Kinder' : 'My Children'}</h2>
        <p className="section-description">
          {isGerman 
            ? 'Speichern Sie Kinderprofile mit Fotos für konsistente Geschichten'
            : 'Save child profiles with photos for consistent story characters'}
        </p>
      </div>

      {/* Profile Grid */}
      <div className="children-profiles-grid">
        {profiles.map((profile) => (
          <div key={profile.id} className="child-profile-card">
            {profile.photo_url ? (
              <div className="profile-photo">
                <img
                  src={profile.photo_url}
                  alt={profile.name}
                  style={{ objectPosition: `center ${profile.photo_position_y ?? 50}%` }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<div class="profile-initial">${profile.name.charAt(0).toUpperCase()}</div>`;
                    e.target.parentElement.classList.add('profile-no-photo');
                  }}
                />
              </div>
            ) : (
              <div className="profile-photo profile-no-photo">
                <div className="profile-initial">{profile.name.charAt(0).toUpperCase()}</div>
              </div>
            )}
            
            {/* Character Illustration Reference */}
            {profile.character_illustration_url && (
              <div className="character-illustration-badge" title={isGerman ? 'Referenzbild für Geschichten' : 'Reference image for stories'}>
                <img 
                  src={profile.character_illustration_url} 
                  alt={isGerman ? 'Referenzbild' : 'Reference'} 
                />
                <span className="illustration-label">🎨</span>
              </div>
            )}
            
            <div className="profile-info">
              <h3>
                {profile.name}
                {profile.relationship && profile.relationship !== 'friend' && (
                  <span className="relationship-icon" title={isGerman ? RELATIONSHIPS[profile.relationship]?.de : RELATIONSHIPS[profile.relationship]?.en}>
                    {RELATIONSHIPS[profile.relationship]?.icon}
                  </span>
                )}
              </h3>
              <p className="profile-age">{isGerman ? 'Alter' : 'Age'}: {profile.age}</p>
              {profile.relationship && (
                <p className="profile-relationship">
                  {isGerman ? RELATIONSHIPS[profile.relationship]?.de : RELATIONSHIPS[profile.relationship]?.en}
                </p>
              )}
              {profile.visual_style && (
                <p className="profile-visual-style">
                  {isGerman ? 'Stil' : 'Style'}: {isGerman ? VISUAL_STYLES[profile.visual_style]?.de : VISUAL_STYLES[profile.visual_style]?.en}
                </p>
              )}
              <div className="profile-actions">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(profile)}
                >
                  {isGerman ? '✏️ Bearbeiten' : '✏️ Edit'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openIllustrationPicker(profile)}
                  title={isGerman ? 'Referenzbild ändern' : 'Change reference image'}
                >
                  🎨
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(profile.id, profile.name)}
                >
                  {isGerman ? '🗑️' : '🗑️'}
                </Button>
              </div>
            </div>
          </div>
        ))}

        {/* Add New Card */}
        <div className="child-profile-card add-new-card" onClick={() => setShowAddModal(true)}>
          <div className="add-icon">+</div>
          <p>{isGerman ? 'Kind hinzufügen' : 'Add Child'}</p>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && createPortal(
        <div className="modal-overlay" onClick={() => !loading && setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingProfile ? (isGerman ? 'Profil bearbeiten' : 'Edit Profile') : (isGerman ? 'Kind hinzufügen' : 'Add Child')}</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row-inline">
                <div className="form-group">
                  <label>{isGerman ? 'Name' : 'Name'}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={isGerman ? 'z.B. Max' : 'e.g. Emma'}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{isGerman ? 'Alter' : 'Age'}</label>
                  <select
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
                    required
                  >
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                    <option value="8">8</option>
                    <option value="9">9</option>
                    <option value="10">10</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Rolle in der Geschichte' : 'Role in the story'}</label>
                <div className="relationship-options">
                  {RELATIONSHIP_OPTIONS.map((key) => {
                    const value = RELATIONSHIPS[key];
                    return (
                    <button
                      key={key}
                      type="button"
                      className={`relationship-btn ${formData.relationship === key ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, relationship: key })}
                    >
                      <span className="rel-icon">{value.icon}</span>
                      <span className="rel-label">{isGerman ? value.de : value.en}</span>
                    </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Darstellungsstil' : 'Visual style'}</label>
                <div className="visual-style-options">
                  {Object.entries(VISUAL_STYLES).map(([key, value]) => (
                    <button
                      key={key}
                      type="button"
                      className={`visual-style-btn ${formData.visual_style === key ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, visual_style: key })}
                    >
                      <span className="style-label">{isGerman ? value.de : value.en}</span>
                      <span className="style-description">{isGerman ? value.descriptionDe : value.descriptionEn}</span>
                    </button>
                  ))}
                </div>
                {editingProfile?.photo_url && (
                  <p className="help-text style-switch-hint">
                    {isGerman
                      ? 'Hinweis: Nach Stilwechsel bitte „Referenzbild neu erzeugen“, damit Cover und Seiten zum neuen Stil passen.'
                      : 'Note: After changing style, regenerate the reference image so cover and pages follow the new style.'}
                  </p>
                )}
                {editingProfile?.photo_url && (
                  <div className="visual-style-action-row">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={regeneratingReference}
                      onClick={() => handleRegenerateReference(editingProfile.id)}
                    >
                      {regeneratingReference
                        ? (isGerman ? 'Erzeuge Referenzbild...' : 'Generating reference image...')
                        : (isGerman ? '🪄 Referenzbild neu erzeugen' : '🪄 Regenerate reference image')}
                    </Button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Foto (optional)' : 'Photo (optional)'}</label>
                {editingProfile && editingProfile.photo_url && !formData.photo && (
                  <div className="current-photo-preview">
                    <img
                      src={editingProfile.photo_url}
                      alt={editingProfile.name}
                      style={{ objectPosition: `center ${formData.photo_position_y ?? 50}%` }}
                    />
                    <p className="help-text">
                      {isGerman ? 'Aktuelles Foto (neues hochladen um zu ändern)' : 'Current photo (upload new to change)'}
                    </p>
                  </div>
                )}
                
                {formData.photo && (
                  <div className="selected-photo-preview">
                    <div className="selected-photo-visual">
                      <img
                        src={selectedPhotoPreviewUrl}
                        alt={isGerman ? 'Neue Vorschau' : 'New preview'}
                        style={{ objectPosition: `center ${formData.photo_position_y ?? 50}%` }}
                      />
                    </div>
                    <div className="preview-header">
                      <span className="photo-name">📷 {formData.photo.name}</span>
                      <button
                        type="button"
                        className="clear-photo-button"
                        onClick={() => setFormData({ ...formData, photo: null })}
                        title={isGerman ? 'Foto entfernen' : 'Remove photo'}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      setFormData({ ...formData, photo: e.target.files[0], description: '' });
                    }
                  }}
                  disabled={formData.description.trim().length > 0}
                  style={{ opacity: formData.description.trim().length > 0 ? 0.5 : 1 }}
                />
                <p className="help-text">
                  {formData.description.trim().length > 0 ? (
                    <span style={{ color: '#ff6b6b', fontWeight: '600' }}>
                      {isGerman ? '⚠️ Beschreibung eingegeben - Foto-Upload deaktiviert' : '⚠️ Description entered - photo upload disabled'}
                    </span>
                  ) : (
                    isGerman ? 'Mit Foto erreichst du die besten Ergebnisse für konsistente Charakterdarstellungen' : 'Photos provide the best results for consistent character representation'
                  )}
                </p>
                <p className="help-text privacy-upload-hint">
                  {isGerman
                    ? 'Datenschutzhinweis: Kinderfotos werden DSGVO-konform verarbeitet und auf Servern in Frankfurt (Deutschland) gespeichert.'
                    : 'Privacy notice: Child photos are processed GDPR-compliant and stored on servers in Frankfurt (Germany).'}
                </p>
                {(formData.photo || (editingProfile && editingProfile.photo_url)) && (
                  <div className="photo-position-control">
                    <div className="photo-position-header">
                      <label>{isGerman ? 'Bildausschnitt verschieben' : 'Adjust visible crop'}</label>
                      <span>{formData.photo_position_y}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={formData.photo_position_y ?? 50}
                      onChange={(e) => setFormData({ ...formData, photo_position_y: parseInt(e.target.value, 10) })}
                    />
                    <p className="help-text">
                      {isGerman
                        ? 'Damit kannst du das Gesicht im runden Ausschnitt höher oder tiefer positionieren.'
                        : 'Move the visible portrait area higher or lower so the face sits better in frame.'}
                    </p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>{isGerman ? 'oder Beschreibung (optional)' : 'or Description (optional)'}</label>
                <div style={{ position: 'relative' }}>
                  <textarea
                    rows="6"
                    value={formData.description}
                    onChange={(e) => {
                      if (!formData.photo) {
                        setFormData({ ...formData, description: e.target.value });
                      }
                    }}
                    placeholder={isGerman ? 'Beschreibe das Kind detailliert: Haarfarbe, Augenfarbe, Gesichtsform, Kleidungsstil, besondere Merkmale... (200-250 Wörter empfohlen)' : 'Describe the child in detail: hair color, eye color, face shape, clothing style, distinctive features... (200-250 words recommended)'}
                    disabled={formData.photo !== null}
                    style={{ 
                      opacity: formData.photo !== null ? 0.5 : 1,
                      backgroundColor: formData.photo !== null ? '#f5f5f5' : 'white'
                    }}
                  />
                  {formData.description && !formData.photo && (
                    <button
                      type="button"
                      className="clear-description-button"
                      onClick={() => setFormData({ ...formData, description: '' })}
                      title={isGerman ? 'Beschreibung löschen' : 'Clear description'}
                    >
                      ×
                    </button>
                  )}
                </div>
                <p className="help-text">
                  {formData.photo ? (
                    <span style={{ color: '#ff6b6b', fontWeight: '600' }}>
                      {isGerman ? '⚠️ Foto hochgeladen - Beschreibung deaktiviert' : '⚠️ Photo uploaded - description disabled'}
                    </span>
                  ) : (
                    isGerman ? 'Entweder Foto oder Beschreibung erforderlich. Detaillierte Beschreibungen (200-250 Wörter) führen zu besseren Ergebnisse.' : 'Either photo or description required. Detailed descriptions (200-250 words) produce better results.'
                  )}
                </p>
              </div>

              <div className="modal-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingProfile(null);
                    setFormData({ name: '', age: 5, photo: null, description: '', relationship: 'main', visual_style: 'realistic', photo_position_y: 50, hair_color: '', eye_color: '' });
                  }}
                  disabled={loading}
                >
                  {isGerman ? 'Abbrechen' : 'Cancel'}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (isGerman ? 'Speichern...' : 'Saving...') : (isGerman ? 'Speichern' : 'Save')}
                </Button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Illustration Picker Modal */}
      {showIllustrationPicker && createPortal(
        <div className="modal-overlay" onClick={() => !loading && setShowIllustrationPicker(null)}>
          <div className="modal-content illustration-picker-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🎨 {isGerman ? 'Referenzbild auswählen' : 'Select Reference Image'}</h3>
            <p className="modal-description">
              {isGerman 
                ? 'Wähle ein Bild aus deinen Geschichten, das als Referenz für zukünftige Geschichten verwendet wird.'
                : 'Choose an image from your stories to use as reference for future stories.'}
            </p>
            
            {loadingImages ? (
              <div className="loading-images">
                <div className="spinner"></div>
                <p>{isGerman ? 'Lade Bilder...' : 'Loading images...'}</p>
              </div>
            ) : storyImages.length === 0 ? (
              <div className="no-images-message">
                <p>{isGerman 
                  ? 'Noch keine Geschichten mit diesem Kind gefunden. Erstelle zuerst eine Geschichte!'
                  : 'No stories found with this child yet. Create a story first!'}
                </p>
              </div>
            ) : (
              <div className="illustration-grid">
                {storyImages.map((img, index) => (
                  <div 
                    key={index} 
                    className="illustration-option"
                    onClick={() => handleSelectIllustration(img.imageUrl)}
                  >
                    <img src={img.imageUrl} alt={img.storyTitle} />
                    <div className="illustration-info">
                      <span className="story-title">{img.storyTitle}</span>
                      <span className="story-date">
                        {new Date(img.createdAt).toLocaleDateString(isGerman ? 'de-DE' : 'en-US')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="modal-actions">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowIllustrationPicker(null);
                  setStoryImages([]);
                }}
                disabled={loading}
              >
                {isGerman ? 'Abbrechen' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
